import { ref, computed, inject } from 'vue'
import { buildProps, definePropType } from '@sgui-plus/utils/props'
import { UPDATE_MODEL_EVENT } from '@sgui-plus/utils/constants'
import { isBool, isString, isNumber } from '@sgui-plus/utils/util'
import { useDisabled, useSize, useSizeProp } from '@sgui-plus/hooks'
import type { ExtractPropTypes, SetupContext } from 'vue'
import { RadioGroupContextKey } from './constants'

export const radioPropsBase = buildProps({
  size: useSizeProp,
  disabled: Boolean,
  label: {
    type: [String, Number, Boolean],
    default: '',
  },
  type: {
    type: String,
    default: 'default',
  },
  beforeChange: {
    type: definePropType<() => Promise<boolean> | boolean>(Function),
  },
})
export const radioProps = buildProps({
  ...radioPropsBase,
  modelValue: {
    type: [String, Number, Boolean],
    default: '',
  },
} as const)
export type RadioProps = ExtractPropTypes<typeof radioProps>

export const radioEmits = {
  [UPDATE_MODEL_EVENT]: (val: string | number | boolean) =>
    isString(val) || isNumber(val) || isBool(val),
  onChange: (val: string | number | boolean) =>
    isString(val) || isNumber(val) || isBool(val),
}
export type RadioEmits = typeof radioEmits

export const useRadio = (
  props: { type: RadioProps['type']; label: RadioProps['label']; modelValue?: RadioProps['modelValue'];  beforeChange?: RadioProps['beforeChange']},
  emit: SetupContext<RadioEmits>['emit']
) => {
  const radioRef = ref<HTMLInputElement>()
  const radioGroup = inject(RadioGroupContextKey, undefined)
  const isGroup = computed(() => !!radioGroup)
  const modelValue = computed<RadioProps['modelValue']>({
    get() {
      return isGroup.value ? radioGroup!.modelValue : props.modelValue!
    },
    set(val) {
      if (isGroup.value) {
        radioGroup!.changeEvent(val)
      } else {
        emit(UPDATE_MODEL_EVENT, val)
      }
      radioRef.value!.checked = props.modelValue === props.label
    },
  })

  const size = useSize(computed(() => radioGroup?.size).value)
  const disabled = useDisabled(computed(() => radioGroup?.disabled).value)
  const focus = ref(false)
  const tabIndex = computed(() => {
    return disabled.value || (isGroup.value && modelValue.value !== props.label)
      ? -1
      : 0
  })
  const beforeChange = props.beforeChange ? props.beforeChange : radioGroup?.beforeChange
  const type = props.type
  return {
    radioRef,
    isGroup,
    radioGroup,
    focus,
    size,
    disabled,
    tabIndex,
    modelValue,
    type,
    beforeChange
  }
}
